import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, ExternalLink, KeyRound, Palette, Save, Settings, User, X } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useI18n } from '../../context/useI18n';
import { emitToast } from '../../context/toast';
import { orgDisplayName, isPlatformSeedGym } from '../../utils/organizationView';
import { userRoles, makeHasAnyRole } from '../dashboard/roles';
import GymMinimalBrandingFields from './GymMinimalBrandingFields';
import OrgOperatorPanel from './OrgOperatorPanel';
import OrgSpiritualConfigPanel from './OrgSpiritualConfigPanel';
import PhoneField from '../ui/PhoneField';
import { mergeLoginHeroIntoSocialLinks } from '../../utils/documentBrand';
import { hasSedeRole } from '../../utils/gymRoleScope';

const TABS = [
  { id: 'general', labelKey: 'orgs.tab.general', fallback: 'General', Icon: User },
  { id: 'marca', labelKey: 'orgs.tab.brand', fallback: 'Marca', Icon: Palette },
  { id: 'admin', labelKey: 'org.operator.title', fallback: 'Administrador', Icon: KeyRound },
  { id: 'config', labelKey: 'orgs.tab.config', fallback: 'Configuración', Icon: Settings },
];

const emptyBranding = {
  logo_url: '',
  brand_name: '',
  brand_slug: '',
  welcome_message: '',
  support_whatsapp: '',
  primary_color: '#2563eb',
  secondary_color: '#10b981',
  white_label_enabled: true,
  favicon_url: '',
  cover_url: '',
  social_links: {},
  custom_domain: '',
  login_line_white: '',
  login_line_yellow_1: '',
  login_line_yellow_2: '',
};

function brandingFromApi(b, fallbackName = '') {
  const links = b?.social_links && typeof b.social_links === 'object' ? b.social_links : {};
  const hero = links.login_hero && typeof links.login_hero === 'object' ? links.login_hero : {};
  return {
    ...emptyBranding,
    logo_url: b.logo_url || '',
    brand_name: b.brand_name || fallbackName || '',
    brand_slug: b.brand_slug || '',
    welcome_message: b.welcome_message || '',
    support_whatsapp: b.support_whatsapp || '',
    primary_color: b.primary_color || '#2563eb',
    secondary_color: b.secondary_color || '#10b981',
    white_label_enabled: b.white_label_enabled !== false,
    favicon_url: b.favicon_url || '',
    cover_url: b.cover_url || '',
    social_links: links,
    custom_domain: b.custom_domain || '',
    login_line_white: hero.line_white || '',
    login_line_yellow_1: hero.line_yellow_1 || '',
    login_line_yellow_2: hero.line_yellow_2 || '',
  };
}

export default function ManageGymOrgModal({
  gym,
  operator,
  open,
  onClose,
  onSaved,
  onArchived,
  onManageLicenses,
  onOpenGymPanel,
  hideArchive = false,
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isSuperAdmin = useMemo(
    () => makeHasAnyRole(userRoles(user))(['super_admin']),
    [user],
  );
  const visibleTabs = useMemo(
    () => TABS.filter(({ id }) => id !== 'config' || isSuperAdmin),
    [isSuperAdmin],
  );
  const [tab, setTab] = useState('general');
  const [general, setGeneral] = useState({ nombre: '', ciudad: '', email: '', telefono: '', sede_plan_id: '' });
  const [branding, setBranding] = useState(emptyBranding);
  const [loadingBrand, setLoadingBrand] = useState(false);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState('');
  const [sedePlans, setSedePlans] = useState([]);

  const displayName = gym ? orgDisplayName(gym) || gym.nombre : '';
  const isSede = Boolean(gym?.parent_gym_id);
  const isSeedGym = isPlatformSeedGym(gym);
  const isSedeAdminActor = useMemo(() => {
    if (!isSede || !user || !gym?.id) return false;
    if (!hasSedeRole(user)) return false;
    const actorGymId = user.gym_id ?? user.gymId;
    return actorGymId != null && Number(actorGymId) === Number(gym.id);
  }, [isSede, user, gym?.id]);

  const loadBranding = useCallback(async () => {
    if (!gym?.id) return;
    setLoadingBrand(true);
    try {
      const { data } = await api.get(`/gyms/${gym.id}/branding`);
      const b = data?.branding || data || {};
      setBranding(brandingFromApi(b, gym.nombre || ''));
    } catch {
      setError(t('wl.load_error', 'No se pudo cargar la marca'));
    } finally {
      setLoadingBrand(false);
    }
  }, [gym?.id, gym?.nombre, t]);

  useEffect(() => {
    if (!open || !gym) return;
    setTab('general');
    setError('');
    const assigned = Array.isArray(gym.available_plan_ids) && gym.available_plan_ids.length
      ? String(gym.available_plan_ids[0])
      : String(gym.plan_id || '');
    setGeneral({
      nombre: gym.nombre || '',
      ciudad: gym.ciudad || '',
      email: (isSedeAdminActor && user?.email) ? user.email : (gym.email || ''),
      telefono: gym.telefono || gym.teléfono || '',
      sede_plan_id: assigned,
    });
    loadBranding();
  }, [open, gym, loadBranding, isSedeAdminActor, user?.email]);

  useEffect(() => {
    if (!open || !gym?.parent_gym_id) {
      setSedePlans([]);
      return undefined;
    }
    let active = true;
    Promise.all([
      api.get('/accounts/plans', {
        params: {
          kind: 'd28d',
          platform_only: 'true',
          include_for_sedes: 'true',
          visible: 'true',
        },
      }).catch(() => ({ data: [] })),
      api.get('/accounts/plans', {
        params: { kind: 'd28d', gym_id: gym.parent_gym_id },
      }).catch(() => ({ data: [] })),
    ]).then(([platRes, gymRes]) => {
      if (!active) return;
      const a = Array.isArray(platRes.data) ? platRes.data : (platRes.data?.data || []);
      const b = Array.isArray(gymRes.data) ? gymRes.data : (gymRes.data?.data || []);
      const map = new Map();
      [...a, ...b].forEach((p) => {
        if (p?.for_sedes === true && p.activo !== false && p.visible !== false) {
          map.set(p.nombre, p);
        }
      });
      setSedePlans([...map.values()]);
    });
    return () => { active = false; };
  }, [open, gym?.id, gym?.parent_gym_id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving && !archiving) onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, saving, archiving]);

  if (!open || !gym) return null;

  const saveGeneral = async (e) => {
    e.preventDefault();
    if (isSede && !general.sede_plan_id) {
      setError(t('orgs.sede_plan_required', 'Debes asignar un plan de sede. Será el único plan visible para los usuarios de esta sede.'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        nombre: general.nombre.trim(),
        ciudad: general.ciudad?.trim() || null,
        email: (isSedeAdminActor ? user?.email : general.email)?.trim()?.toLowerCase() || null,
        telefono: general.telefono?.trim() || null,
      };
      if (isSede && general.sede_plan_id) {
        payload.available_plan_ids = [general.sede_plan_id];
        payload.plan_id = general.sede_plan_id;
      }
      const { data } = await api.put(`/gyms/${gym.id}`, payload);
      const updated = data?.gym || {};
      setGeneral({
        nombre: updated.nombre || general.nombre,
        ciudad: updated.ciudad ?? general.ciudad,
        email: updated.email ?? general.email,
        telefono: updated.telefono || updated.teléfono || general.telefono,
        sede_plan_id: Array.isArray(updated.available_plan_ids) && updated.available_plan_ids[0]
          ? String(updated.available_plan_ids[0])
          : general.sede_plan_id,
      });
      emitToast({ type: 'success', title: t('orgs.gym_updated', 'Organización actualizada') });
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || t('orgs.gym_update_error', 'No se pudo actualizar'));
    } finally {
      setSaving(false);
    }
  };

  const saveBranding = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...branding };
      if (!String(payload.brand_slug || '').trim()) delete payload.brand_slug;
      payload.social_links = mergeLoginHeroIntoSocialLinks(branding.social_links, {
        line_white: branding.login_line_white,
        line_yellow_1: branding.login_line_yellow_1,
        line_yellow_2: branding.login_line_yellow_2,
      });
      delete payload.login_line_white;
      delete payload.login_line_yellow_1;
      delete payload.login_line_yellow_2;
      const { data } = await api.put(`/gyms/${gym.id}/branding`, payload);
      const b = data?.branding || {};
      setBranding(brandingFromApi(b, general.nombre || ''));
      emitToast({ type: 'success', title: t('wl.saved', 'Marca guardada') });
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || t('wl.save_error', 'Error al guardar marca'));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    const msg = t('orgs.archive_gym_confirm', '¿Archivar esta organización? Desaparecerá de listados y selectores, pero se conservará el histórico.');
    if (!window.confirm(msg)) return;
    setArchiving(true);
    setError('');
    try {
      await api.delete(`/gyms/${gym.id}`);
      emitToast({ type: 'success', title: t('orgs.gym_archived', 'Organización archivada') });
      onArchived?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || t('orgs.gym_archive_error', 'No se pudo archivar'));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="form-modal-overlay" style={{ zIndex: 9998 }} role="dialog" aria-modal="true" onClick={() => !saving && !archiving && onClose?.()}>
      <div className="form-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-header">
          <div className="min-w-0">
            <h3 className="font-bold truncate">
              {t('orgs.manage_gym', 'Gestionar organización')}: {displayName}
            </h3>
            <p className="text-xs text-stone-500 mt-0.5">
              {isSede ? t('orgs.type.sede', 'Sede') : t('orgs.type.virtual_gym', 'Gimnasio virtual')}
              {general.email ? ` · ${general.email}` : ''}
            </p>
          </div>
          <button type="button" className="form-close-btn shrink-0" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="px-4 pt-3 flex flex-wrap gap-2 border-b border-slate-200">
          {visibleTabs.map(({ id, labelKey, fallback, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                tab === id ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t(labelKey, fallback)}
            </button>
          ))}
        </div>

        <div className="form-modal-content">
          {isSeedGym ? (
            <p className="text-amber-800 text-sm mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {t('orgs.seed_gym_no_archive', 'Este gimnasio es ancla de plataforma D28D y no puede archivarse.')}
            </p>
          ) : null}
          {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}

          {tab === 'general' && (
            <form onSubmit={saveGeneral} className="space-y-4">
              <label className="block text-sm">
                <span className="font-semibold">{t('tenants.field_name', 'Nombre')}</span>
                <input className="input mt-1 w-full" required value={general.nombre} onChange={(e) => setGeneral((p) => ({ ...p, nombre: e.target.value }))} />
              </label>
              {isSede ? (
                <label className="block text-sm">
                  <span className="font-semibold">{t('orgs.sede_plan', 'Plan de la sede')} *</span>
                  <select
                    className="input mt-1 w-full"
                    required
                    value={general.sede_plan_id || ''}
                    onChange={(e) => setGeneral((p) => ({ ...p, sede_plan_id: e.target.value }))}
                  >
                    <option value="">{t('orgs.sede_plan_placeholder', 'Selecciona un plan de sedes')}</option>
                    {general.sede_plan_id
                      && !sedePlans.some((p) => p.nombre === general.sede_plan_id) ? (
                        <option value={general.sede_plan_id}>{general.sede_plan_id}</option>
                      ) : null}
                    {sedePlans.map((p) => (
                      <option key={p.nombre} value={p.nombre}>
                        {p.display_nombre || p.nombre}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-stone-500 mt-0.5 block">
                    {t('orgs.sede_plan_hint', 'Obligatorio. Solo este plan se mostrará a los usuarios finales de la sede.')}
                  </span>
                </label>
              ) : null}
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="font-semibold">{t('tenants.field_city', 'Ciudad')}</span>
                  <input className="input mt-1 w-full" value={general.ciudad} onChange={(e) => setGeneral((p) => ({ ...p, ciudad: e.target.value }))} />
                </label>
                <PhoneField
                  label={t('common.phone', 'Teléfono')}
                  value={general.telefono}
                  onChange={(v) => setGeneral((p) => ({ ...p, telefono: v }))}
                  inputClassName="input mt-1 w-full"
                  className="block text-sm"
                  labelClassName="font-semibold"
                />
              </div>
              <label className="block text-sm">
                <span className="font-semibold">{t('common.email', 'Email')}</span>
                <input
                  className="input mt-1 w-full"
                  type="email"
                  value={isSedeAdminActor ? (user?.email || general.email) : general.email}
                  onChange={(e) => setGeneral((p) => ({ ...p, email: e.target.value }))}
                  readOnly={isSedeAdminActor}
                  disabled={isSedeAdminActor}
                />
                {isSedeAdminActor ? (
                  <span className="text-xs text-stone-500 mt-0.5 block">
                    {t('orgs.sede_contact_email_login', 'Contacto de la sede = correo de tu cuenta admin. Para cambiarlo usa Mi Cuenta o Administrador.')}
                  </span>
                ) : null}
              </label>
              <div className="form-modal-footer">
                <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={saving}>
                  <Save size={16} />
                  {saving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}
                </button>
              </div>
            </form>
          )}

          {tab === 'marca' && (
            loadingBrand ? (
              <p className="text-stone-500">{t('common.loading', 'Cargando…')}</p>
            ) : (
              <form onSubmit={saveBranding} className="space-y-4">
                <GymMinimalBrandingFields formData={branding} setFormData={setBranding} showBrandName showLoginHero gymId={gym.id} />
                <label className="block text-sm">
                  <span className="font-semibold">{t('gym.brand_slug', 'Slug URL pública')}</span>
                  <input
                    className="input mt-1 w-full font-mono text-sm"
                    value={branding.brand_slug || ''}
                    onChange={(e) => setBranding((p) => ({ ...p, brand_slug: e.target.value }))}
                    placeholder="strongfit"
                  />
                  <span className="text-xs text-stone-500 mt-0.5 block">
                    {branding.brand_slug
                      ? `Landing: /gym/${branding.brand_slug} · Login: /gym/${branding.brand_slug}/login`
                      : 'Define el path público (ej. strongfit → /gym/strongfit/login).'}
                  </span>
                </label>
                <div className="form-modal-footer">
                  <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={saving}>
                    <Save size={16} />
                    {saving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar marca')}
                  </button>
                </div>
              </form>
            )
          )}

          {tab === 'admin' && (
            <OrgOperatorPanel entityType="gym" entityId={gym.id} />
          )}

          {tab === 'config' && isSuperAdmin && (
            <OrgSpiritualConfigPanel
              entityType="gym"
              entityId={gym.id}
              isSede={isSede}
              onSaved={onSaved}
            />
          )}
        </div>

        <div className="px-4 pb-4 flex flex-wrap gap-2 justify-between border-t border-slate-200 pt-4">
          <div className="flex flex-wrap gap-2">
            {operator && onManageLicenses ? (
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => onManageLicenses({ operator, orgName: displayName })}
              >
                {t('orgs.licenses', 'Licencias')}
              </button>
            ) : null}
            {onOpenGymPanel ? (
              <button
                type="button"
                className="btn-secondary text-sm inline-flex items-center gap-1.5"
                onClick={() => {
                  onOpenGymPanel({ id: gym.id, name: displayName });
                  onClose?.();
                }}
              >
                <ExternalLink className="w-4 h-4" />
                {t('orgs.open_d28d_panel', 'Panel operativo D28D')}
              </button>
            ) : null}
          </div>
          {!hideArchive && !isSeedGym ? (
            <button
              type="button"
              className="btn-danger text-sm inline-flex items-center gap-1.5"
              disabled={archiving || saving}
              onClick={handleArchive}
            >
              <Archive className="w-4 h-4" />
              {archiving ? t('common.saving', 'Guardando…') : t('orgs.archive', 'Archivar')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
